// src/components/AdminStudManager/AdminStudManager.js 
import React, { useCallback, useEffect, useState } from "react";
import RequireAdmin from "../RequireAdmin";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase";
import "./AdminStudManager.css";

export default function AdminStudManager() {
  const empty = {
    name: "",
    available: true,
    studFee: "",
    ofaCertified: false,
    registration: "",
    dob: "",
    color: "",
    notes: "",
  };

  const [studs, setStuds] = useState([]);
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  // filter mode: all | available | hidden
  const [filterMode, setFilterMode] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = await getDocs(collection(db, "studs"));
      let rows = qs.docs.map((d) => ({ id: d.id, ...d.data() }));
      if (filterMode === "available") {
        rows = rows.filter((r) => r.available);
      } else if (filterMode === "hidden") {
        rows = rows.filter((r) => !r.available);
      }
      setStuds(rows);
    } finally {
      setLoading(false);
    }
  }, [filterMode]);

  useEffect(() => {
    load();
  }, [load]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let imageUrl = null;
      if (file) {
        const imgRef = ref(storage, `studs/${Date.now()}_${file.name}`);
        await uploadBytes(imgRef, file);
        imageUrl = await getDownloadURL(imgRef);
      }
      await addDoc(collection(db, "studs"), {
        name: form.name.trim(),
        available: !!form.available,
        studFee: form.available ? form.studFee : "",
        ofaCertified: !!form.ofaCertified,
        registration: form.registration.trim(),
        dob: form.dob,
        color: form.color.trim(),
        notes: form.notes.trim(),
        imageUrl,
        createdAt: new Date(),
      });
      setForm(empty);
      setFile(null);
      await load();
      alert("Stud created");
    } catch (e) {
      console.error(e);
      alert("Create failed");
    } finally {
      setLoading(false);
    }
  };

  const quickUpdate = async (id, patch) => {
    setLoading(true);
    try {
      await updateDoc(doc(db, "studs", id), patch);
      await load();
    } catch (e) {
      console.error(e);
      alert("Update failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete ${row.name}?`)) return;
    setLoading(true);
    try {
      await deleteDoc(doc(db, "studs", row.id));
      await load();
    } catch (e) {
      console.error(e);
      alert("Delete failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <RequireAdmin>
      <div className="adminStud">
        <h1>Admin Stud Manager</h1>

        {/* view toggle */}
        <div className="actions" style={{ marginBottom: 16 }}>
          <button
            onClick={() => setFilterMode("all")}
            className={`btn ${filterMode === "all" ? "" : "btnLight"}`}
          >
            Show All
          </button>
          <button
            onClick={() => setFilterMode("available")}
            className={`btn ${filterMode === "available" ? "" : "btnLight"}`}
          >
            Show Available Only
          </button>
          <button
            onClick={() => setFilterMode("hidden")}
            className={`btn ${filterMode === "hidden" ? "" : "btnLight"}`}
          >
            Show Hidden Only
          </button>
        </div>

        {/* form */}
        <form onSubmit={handleCreate} className="panel">
          <h3>Add Stud</h3>
          <div className="gridTwo">
            <label>
              Name
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                required
              />
            </label>

            <label>
              Stud Fee
              <input
                name="studFee"
                type="number"
                value={form.studFee}
                onChange={handleChange}
                disabled={!form.available}
              />
            </label>

            <label>
              Registration
              <input
                name="registration"
                value={form.registration}
                onChange={handleChange}
              />
            </label>

            <label>
              Date of Birth
              <input
                name="dob"
                type="date"
                value={form.dob}
                onChange={handleChange}
              />
            </label>

            <label>
              Color
              <input
                name="color"
                value={form.color}
                onChange={handleChange}
              />
            </label>

            <label className="checkRow full">
              <input
                type="checkbox"
                name="available"
                checked={form.available}
                onChange={handleChange}
              />
              Available
            </label>

            <label className="checkRow full">
              <input
                type="checkbox"
                name="ofaCertified"
                checked={form.ofaCertified}
                onChange={handleChange}
              />
              OFA Certified
            </label>

            <label>
              Photo
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </label>
          </div>

          <label className="footerRow">
            Notes
            <textarea
              name="notes"
              rows={3}
              value={form.notes}
              onChange={handleChange}
            />
          </label>

          <div className="actions footerRow">
            <button type="submit" disabled={loading} className="btn">
              {loading ? "Saving..." : "Add Stud"}
            </button>
          </div>
        </form>

        {/* list */}
        <h3>Current Studs</h3>
        {loading && <div>Loading...</div>}
        {!loading && studs.length === 0 && <div>No studs yet.</div>}
        <div className="list">
          {studs.map((s) => (
            <div key={s.id} className="card">
              <div>
                {s.imageUrl ? (
                  <img src={s.imageUrl} alt={s.name} className="thumb" />
                ) : (
                  <div className="noThumb">No image</div>
                )}
              </div>
              <div className="meta">
                <div style={{ fontWeight: 600 }}>{s.name}</div>
                <div>Available: {String(s.available)}</div>
                {s.studFee && <div>Stud Fee: ${s.studFee}</div>}
                {s.dob && <div>DOB: {s.dob}</div>}
                {s.color && <div>Color: {s.color}</div>}
                {s.registration && <div>Registration: {s.registration}</div>}
                {typeof s.ofaCertified !== "undefined" && (
                  <div>OFA Certified: {s.ofaCertified ? "Yes" : "No"}</div>
                )}
                {s.notes && <div>{s.notes}</div>}
              </div>
              <div className="actions">
                <button
                  className="btn btnLight"
                  onClick={() => quickUpdate(s.id, { available: !s.available })}
                >
                  Toggle Available
                </button>
                <button
                  className="btn btnDanger"
                  onClick={() => handleDelete(s)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </RequireAdmin>
  );
}
